/**
 * Command history for the geometry editors.
 *
 * Every change is recorded as a named command with the model it produced.
 * Naming matters: "Undo move point" tells a user what is about to happen,
 * where a bare undo button does not. Recording the domain model rather than
 * component state keeps the history serializable and testable, and stops
 * unrelated interface state from being restored along with the geometry.
 */

import { useCallback, useMemo, useState } from 'react';

export interface HistoryEntry<T> {
  label: string;
  model: T;
}

export interface History<T> {
  present: T;
  past: HistoryEntry<T>[];
  future: HistoryEntry<T>[];
}

/** The number of steps kept. Beyond this the oldest are dropped. */
export const HISTORY_LIMIT = 100;

export function createHistory<T>(initial: T): History<T> {
  return { present: initial, past: [], future: [] };
}

/**
 * Records a change.
 *
 * A command that leaves the model unchanged is not recorded. Without that,
 * clicking a point without moving it would fill the history with steps that
 * appear to do nothing when undone.
 */
export function apply<T>(history: History<T>, label: string, next: T): History<T> {
  if (next === history.present) return history;
  const past = [...history.past, { label, model: history.present }];
  return {
    present: next,
    past: past.length > HISTORY_LIMIT ? past.slice(past.length - HISTORY_LIMIT) : past,
    // A new command discards the redo branch: the future it led to no longer
    // follows from the present.
    future: [],
  };
}

/**
 * Replaces the present without recording a step.
 *
 * Not every change belongs in the history. A project's name or location is
 * not an engineering input, and recording it would put a step in the stack
 * that appears to do nothing when undone. Such a change still has to reach
 * the present, which is what this does.
 */
export function replacePresent<T>(history: History<T>, next: T): History<T> {
  if (next === history.present) return history;
  return { ...history, present: next };
}

export function undo<T>(history: History<T>): History<T> {
  const previous = history.past[history.past.length - 1];
  if (!previous) return history;
  return {
    present: previous.model,
    past: history.past.slice(0, -1),
    future: [{ label: previous.label, model: history.present }, ...history.future],
  };
}

export function redo<T>(history: History<T>): History<T> {
  const [next, ...rest] = history.future;
  if (!next) return history;
  return {
    present: next.model,
    past: [...history.past, { label: next.label, model: history.present }],
    future: rest,
  };
}

export function undoLabel<T>(history: History<T>): string | undefined {
  return history.past[history.past.length - 1]?.label;
}

export function redoLabel<T>(history: History<T>): string | undefined {
  return history.future[0]?.label;
}

/** React binding for the history above. */
export function useHistory<T>(initial: T) {
  const [history, setHistory] = useState<History<T>>(() => createHistory(initial));

  const run = useCallback((label: string, produce: (current: T) => T) => {
    setHistory((current) => apply(current, label, produce(current.present)));
  }, []);

  const reset = useCallback((model: T) => {
    setHistory(createHistory(model));
  }, []);

  const replace = useCallback((next: T) => {
    setHistory((current) => replacePresent(current, next));
  }, []);

  return useMemo(() => ({
    model: history.present,
    run,
    reset,
    replace,
    undo: () => setHistory(undo),
    redo: () => setHistory(redo),
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    undoLabel: undoLabel(history),
    redoLabel: redoLabel(history),
    // The models undo and redo would restore, so a caller can see what is
    // about to change before changing it.
    undoModel: history.past[history.past.length - 1]?.model,
    redoModel: history.future[0]?.model,
    depth: history.past.length,
  }), [history, run, reset, replace]);
}
